import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import './KDPusers.css';
import { Users, Mail, Phone, MapPin, Eye, EyeOff, Search, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import apiWithAuth from '../../services/apiWithAuth';
import { usePermissions } from '../../hooks/usePermissions';

const roleOptions = [
    'ALL_ROLES', // Custom value for "All Roles"
    'ShopOwner',
    'DeliveryBoy',
    'ShopMember',
    'Admin',
    'Guest',
];

const roleLabels = {
    ALL_ROLES: 'All Roles',
    ShopOwner: 'Shop Owner',
    DeliveryBoy: 'Delivery Boy',
    ShopMember: 'Shop Member',
    Admin: 'Admin',
    Guest: 'Guest',
};

// Colored tags for roles
const getRoleClass = (role) => {
    switch (role) {
        case 'ShopOwner': return 'KDP-role-shopowner';
        case 'DeliveryBoy': return 'KDP-role-deliveryboy';
        case 'ShopMember': return 'KDP-role-shopmember';
        case 'Admin': return 'KDP-role-admin';
        case 'Guest': return 'KDP-role-guest';
        default: return 'KDP-role-default';
    }
};

/** Format address for display: address object (buildingNo, street, landmark, city, state, pincode) → fullAddress → NA */
const formatAddress = (user) => {
    const addr = user?.address;
    if (addr && typeof addr === 'object' && !Array.isArray(addr)) {
        const parts = [addr.buildingNo, addr.street, addr.landmark, addr.city, addr.state, addr.pincode].filter(Boolean);
        if (parts.length) return parts.join(', ');
    }
    if (typeof addr === 'string' && addr) return addr;
    if (user?.fullAddress) return user.fullAddress;
    if (user?.addressLine1 || user?.city || user?.state || user?.pincode) {
        return [user.addressLine1, user.city, user.state, user.pincode].filter(Boolean).join(', ') || '';
    }
    return '';
};

const KDPUsersPanel = () => {
    const permissions = usePermissions('KDP Users');
    const [searchParams, setSearchParams] = useSearchParams();
    const urlRole = searchParams.get('role');
    
    // Set initial role based on URL query parameter
    const initialRole = urlRole && roleOptions.includes(urlRole) ? urlRole : 'ALL_ROLES';
    
    const [data, setData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    const [selectedRole, setSelectedRole] = useState(initialRole);
    const [showNewOnly, setShowNewOnly] = useState(false);
    const [expandedUser, setExpandedUser] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deletePopup, setDeletePopup] = useState({ show: false, user: null });

    const PAGE_LIMIT = 20;

    const fetchUsers = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                page: page.toString(),
                limit: PAGE_LIMIT.toString(),
            });
            if (selectedStatus !== 'ALL') params.append('status', selectedStatus);
            if (selectedRole !== 'ALL_ROLES') params.append('role', selectedRole);
            if (searchTerm.trim()) params.append('search', searchTerm.trim());
            if (showNewOnly) params.append('new', 'true');

            const response = await apiWithAuth.get(`/admin-panel/KDPusers/all?${params}`);

            if (response.data) {
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (err) {
            console.error('Error fetching KDP users:', err);
            setError(err.response?.data?.message || err.message || 'Failed to fetch users');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [selectedStatus, selectedRole, searchTerm, showNewOnly]);

    useEffect(() => {
        fetchUsers(currentPage);
    }, [fetchUsers, currentPage]);

    // Update selectedRole when URL changes
    useEffect(() => {
        if (urlRole && roleOptions.includes(urlRole)) {
            setSelectedRole(urlRole);
            setSelectedStatus('ALL');
            setShowNewOnly(false);
            setSearchTerm('');
            setCurrentPage(1);
        } else if (!urlRole) {
            setSelectedRole('ALL_ROLES');
            setSelectedStatus('ALL');
            setShowNewOnly(false);
            setSearchTerm('');
            setCurrentPage(1);
        }
    }, [urlRole]);

    // Delete confirmation popup
    const showDeletePopup = (user) => {
        setDeletePopup({ show: true, user });
    };

    const handleDeleteUser = async () => {
        const { user } = deletePopup;

        try {
            setLoading(true);

            await apiWithAuth.delete(`/users/single-profile/hard?userId=${user._id}`);

            setDeletePopup({ show: false, user: null });
            fetchUsers(currentPage);
            setTimeout(() => alert(`User "${user.fullName}" has been successfully deleted.`), 100);
        } catch (err) {
            console.error('Error deleting user:', err);
            alert(`Error deleting user "${user.fullName}": ${err.response?.data?.message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const closeDeletePopup = () => {
        setDeletePopup({ show: false, user: null });
    };

    if (loading && !data) return <div className="KDP-users-loading">Loading users...</div>;
    if (error && !data) return <div className="KDP-users-error">Error: {error}</div>;
    if (!data) return <div className="KDP-users-error">No data available</div>;

    const pagination = data.pagination || {
        currentPage: 1,
        totalPages: 1,
        limit: PAGE_LIMIT,
        hasNextPage: false,
        hasPrevPage: false,
    };
    const statusCounts = data.statusCounts || {};
    const users = data.users || [];
    const itemsStart = users.length > 0 ? ((pagination.currentPage - 1) * pagination.limit) + 1 : 0;
    const itemsEnd = users.length > 0 ? itemsStart + users.length - 1 : 0;

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getStatusClass = (status) => {
        switch (status) {
            case 'ACCEPTED': return 'KDP-users-status-accepted';
            case 'PENDING': return 'KDP-users-status-pending';
            case 'REJECTED': return 'KDP-users-status-rejected';
            case 'HOLD': return 'KDP-users-status-hold';
            default: return 'KDP-users-status-default';
        }
    };

    // Get counts of users per role from actual data
    // const getRoleCounts = () => {
    //     if (!data || !data.users) return {};
    //     const counts = {};
    //     data.users.forEach(user => {
    //         const role = user.role || 'Guest';
    //         counts[role] = (counts[role] || 0) + 1;
    //     });
    //     return counts;
    // };
    // const roleCounts = getRoleCounts();

    const getStatusCount = (status) => statusCounts[status] || 0;

    const getAllStatusCount = () => {
        const totalFromCounts = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
        return data.totalUsers ?? totalFromCounts;
    };

    const getNewUsersCount = () => users.filter(user => user.new).length;

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        });

    const openImagePopup = (imageUrl) => setSelectedImage(imageUrl);
    const closeImagePopup = () => setSelectedImage(null);

    // Get display title based on selected role
    const getTitleText = () => {
        if (selectedRole === 'ALL_ROLES') {
            return 'KDP Users';
        }
        return roleLabels[selectedRole] || selectedRole;
    };

    return (
        <>
            <div className="KDP-users-container">
                {/* Header Stats */}
                <div className="KDP-users-header">
                    <h1 className="KDP-users-title">
                        <Users size={24} />
                        {getTitleText()} ({data.totalUsers ?? getAllStatusCount()})
                    </h1>

                    {/* Results Count Display */}
                    <div className="KDP-users-results-count" style={{ marginBottom: 15, fontSize: '14px', color: '#666' }}>
                        Showing {itemsStart > 0 ? `${itemsStart} to ${itemsEnd}` : '0'} of {data.totalUsers ?? getAllStatusCount()} users
                        {(selectedStatus !== 'ALL' || selectedRole !== 'ALL_ROLES' || showNewOnly || searchTerm) && (
                            <span> (filtered)</span>
                        )}
                    </div>

                    {/* Status Filters */}
                    <div className="KDP-users-filters filter-group">
                        <div className="filter-label">Status:</div>
                        <button
                            onClick={() => { setSelectedStatus('ALL'); setCurrentPage(1); }}
                            className={`KDP-users-filter-btn ${selectedStatus === 'ALL' ? 'active' : ''}`}
                        >
                            All ({getAllStatusCount()})
                        </button>
                        {Object.entries(statusCounts).map(([status]) => (
                            <button
                                key={status}
                                onClick={() => { setSelectedStatus(status); setCurrentPage(1); }}
                                className={`KDP-users-filter-btn ${getStatusClass(status)} ${selectedStatus === status ? 'active' : ''}`}
                            >
                                {status} ({getStatusCount(status)})
                            </button>
                        ))}
                        <button
                            onClick={() => { setShowNewOnly(!showNewOnly); setCurrentPage(1); }}
                            className={`KDP-users-filter-btn KDP-users-new-filter ${showNewOnly ? 'active' : ''}`}
                        >
                            New Users ({getNewUsersCount()})
                        </button>
                    </div>

                    {/* Role Filters - Dropdown */}
                    <div className="KDP-users-filters filter-group" style={{ marginTop: 12 }}>
                        <div className="filter-label">Role:</div>
                        <select
                            value={selectedRole}
                            onChange={(e) => {
                                const newRole = e.target.value;
                                setSelectedRole(newRole);
                                setCurrentPage(1);
                                if (newRole === 'ALL_ROLES') {
                                    searchParams.delete('role');
                                    setSearchParams(searchParams);
                                } else {
                                    setSearchParams({ role: newRole });
                                }
                            }}
                            className="KDP-role-select"
                            style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                fontSize: '14px',
                                cursor: 'pointer',
                                backgroundColor: '#fff'
                            }}
                        >
                            {roleOptions.map((role) => (
                                <option key={role} value={role}>
                                    {roleLabels[role]}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Search Bar */}
                    <div className="KDP-users-search" style={{ marginTop: 15 }}>
                        <Search className="KDP-users-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by name, email, phone, or address..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            className="KDP-users-search-input"
                        />
                    </div>
                </div>

                {/* Users List */}
                <div className={`KDP-users-list ${loading ? 'KDP-users-list-loading' : ''}`}>
                    {loading && data && (
                        <div className="KDP-users-page-loading">Loading page...</div>
                    )}
                    {users.map(user => (
                        <div key={user._id} className="KDP-users-card">
                            <div className="KDP-users-main-row">
                                <div className="KDP-users-left-section">
                                    <div className="KDP-users-avatar">
                                        {user.profileImageUrl ? (
                                            <img
                                                src={user.profileImageUrl}
                                                alt={user.fullName}
                                                className="KDP-users-avatar-img"
                                                onClick={() => openImagePopup(user.profileImageUrl)}
                                            />
                                        ) : (
                                            user.fullName?.charAt(0).toUpperCase()
                                        )}
                                    </div>
                                    <div className="KDP-users-info">
                                        <div className="KDP-users-name-row">
                                            <h3 className="KDP-users-name">{user.fullName}</h3>
                                            {user.new && <span className="KDP-users-new-badge">NEW</span>}
                                            <span className={`KDP-users-badge ${getStatusClass(user.status)}`}>
                                                {user.status}
                                            </span>
                                            {/* Role badge */}
                                            <span className={`KDP-users-badge KDP-users-role ${getRoleClass(user.role)}`}>
                                                {user.role}
                                            </span>
                                            {/* UserType badge */}
                                            <span className="KDP-users-badge KDP-users-usertype">
                                                {user.userType}
                                            </span>
                                        </div>
                                        <div className="KDP-users-contact-row">
                                            <span className="KDP-users-contact-item">
                                                <Mail size={12} />
                                                {user.email ? (
                                                    <>
                                                        <span className="KDP-users-contact-text">{user.email}</span>
                                                        {user.emailVerified && (
                                                            <img src="/email-verified" alt="Email Verified" className="KDP-users-verified" />
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="KDP-users-contact-text">N/A</span>
                                                )}
                                            </span>
                                            <span className="KDP-users-contact-item">
                                                {user.phone ? (
                                                    <>
                                                        <Phone size={12} />
                                                        <span className="KDP-users-contact-text">{user.phone}</span>
                                                        {user.phoneVerified && (
                                                            <img src="/phone-verified.png" alt="Phone Verified" className="KDP-users-verified" />
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="KDP-users-contact-text">N/A</span>
                                                )}
                                            </span>
                                            <span className="KDP-users-contact-item">
                                                <MapPin size={12} />
                                                <span className="KDP-users-contact-text">
                                                    {formatAddress(user) || 'N/A'}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="KDP-users-right-section">
                                    {permissions.canDelete && (
                                        <button
                                            onClick={() => showDeletePopup(user)}
                                            className="KDP-users-delete-btn"
                                            title="Delete User"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                    <span className="KDP-users-date">
                                        {user.lastLogin ? formatDate(user.lastLogin) : 'Never Logged In'}
                                    </span>
                                    <button
                                        onClick={() => setExpandedUser(expandedUser === user._id ? null : user._id)}
                                        className="KDP-users-expand-btn"
                                    >
                                        {expandedUser === user._id ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {expandedUser === user._id && (
                                <div className="KDP-users-details">
                                    <div className="KDP-users-details-grid">
                                        <div className="KDP-users-detail-item">
                                            <span className="KDP-users-detail-label">Role</span>
                                            <span className="KDP-users-detail-value">{user.role || 'N/A'}</span>
                                        </div>
                                        <div className="KDP-users-detail-item">
                                            <span className="KDP-users-detail-label">User Type</span>
                                            <span className="KDP-users-detail-value">{user.userType || 'N/A'}</span>
                                        </div>
                                        <div className="KDP-users-detail-item">
                                            <span className="KDP-users-detail-label">Created</span>
                                            <span className="KDP-users-detail-value">
                                                {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="KDP-users-detail-item">
                                            <span className="KDP-users-detail-label">Last Login</span>
                                            <span className="KDP-users-detail-value">
                                                {user.lastLogin ? formatDate(user.lastLogin) : 'never logged in'}
                                            </span>
                                        </div>
                                        <div className="KDP-users-detail-item">
                                            <span className="KDP-users-detail-label">Devices</span>
                                            <span className="KDP-users-detail-value">
                                                {user.loggedInDevicesCount != null ? user.loggedInDevicesCount : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="KDP-users-detail-item">
                                            <span className="KDP-users-detail-label">Failed Logins</span>
                                            <span className="KDP-users-detail-value">
                                                {user.failedLoginAttempts != null ? user.failedLoginAttempts : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="KDP-users-detail-item">
                                            <span className="KDP-users-detail-label">Address</span>
                                            <span className="KDP-users-detail-value">
                                                {formatAddress(user) || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Empty state */}
                {!loading && users.length === 0 && (
                    <div className="KDP-users-empty">
                        No users found for the selected filters.
                    </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                    <div className="KDP-users-pagination">
                        <button
                            className="KDP-users-pagination-btn"
                            disabled={!pagination.hasPrevPage || loading}
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                        >
                            <ChevronLeft size={16} />
                            Previous
                        </button>

                        <span className="KDP-users-pagination-info">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>

                        <button
                            className="KDP-users-pagination-btn"
                            disabled={!pagination.hasNextPage || loading}
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    </div>
                )}

                {/* Image Popup */}
                {selectedImage && (
                    <div className="image-popup-overlay" onClick={closeImagePopup}>
                        <div className="image-popup-content" onClick={e => e.stopPropagation()}>
                            <button className="image-popup-close" onClick={closeImagePopup}>×</button>
                            <img src={selectedImage} alt="Document" className="image-popup-img" />
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Popup */}
                {deletePopup.show && (
                    <div className="KDP-delete-popup-overlay" onClick={closeDeletePopup}>
                        <div className="KDP-delete-popup-content" onClick={e => e.stopPropagation()}>
                            <div className="KDP-delete-popup-header">
                                <div className="KDP-delete-popup-icon">
                                    <Trash2 size={24} />
                                </div>
                                <h3>Delete User</h3>
                            </div>
                            <div className="KDP-delete-popup-body">
                                <p>Are you sure you want to delete this user?</p>
                                <div className="KDP-delete-user-info">
                                    <div className="KDP-delete-user-avatar">
                                        {deletePopup.user?.profileImageUrl ? (
                                            <img src={deletePopup.user.profileImageUrl} alt={deletePopup.user.fullName} />
                                        ) : (
                                            deletePopup.user?.fullName?.charAt(0).toUpperCase()
                                        )}
                                    </div>
                                    <div>
                                        <div className="KDP-delete-user-name">{deletePopup.user?.fullName}</div>
                                        <div className="KDP-delete-user-email">{deletePopup.user?.email || 'No email'}</div>
                                        <div className="KDP-delete-user-phone">{deletePopup.user?.phone || 'No phone'}</div>
                                    </div>
                                </div>
                                <p className="KDP-delete-warning">This action cannot be undone.</p>
                            </div>
                            <div className="KDP-delete-popup-footer">
                                <button onClick={closeDeletePopup} className="KDP-delete-popup-cancel">
                                    Cancel
                                </button>
                                <button onClick={handleDeleteUser} className="KDP-delete-popup-confirm" disabled={loading}>
                                    {loading ? 'Deleting...' : 'Delete User'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default KDPUsersPanel;

